export interface CommunicationFileRecord {
    id: number;
    path?: string | null;
    name?: string | null;
}

export interface NewsletterRecord {
    id: number;
    name?: string | null;
    title?: string | null;
    content?: string | null;
    files?: CommunicationFileRecord[];
    created_at?: string | null;
    updated_at?: string | null;
}

export interface SubscriberRecord {
    id: number;
    email: string;
    active?: boolean | number | null;
    created_at?: string | null;
    updated_at?: string | null;
}

export interface NewsletterFormState {
    name: string;
    title: string;
    content: string;
    imageIds: string;
}

export interface SubscriberFormState {
    email: string;
    active: boolean;
}

interface ValidNewsletterForm {
    name: string;
    title: string;
    content: string;
    image_id?: number[];
}

interface ValidSubscriberForm {
    email: string;
    active: boolean;
}

type ValidationResult<T> =
    | { valid: true; data: T; }
    | { valid: false; message: string; };

const asRecord = (value: unknown): Record<string, any> | null => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    return value as Record<string, any>;
};

const extractApiPayload = (response: any): unknown => {
    const data = response?.data;
    const record = asRecord(data);

    if (record && 'data' in record) {
        return record.data;
    }

    return data ?? response;
};

const extractItemsArray = <T>(payload: unknown): T[] => {
    if (Array.isArray(payload)) {
        return payload as T[];
    }

    const record = asRecord(payload);

    if (record && Array.isArray(record.data)) {
        return record.data as T[];
    }

    return [];
};

const sortByNewest = <T extends { id?: number; created_at?: string | null; updated_at?: string | null; }>(items: T[]): T[] =>
    [...items].sort((left, right) => {
        const leftTime = Date.parse(left.updated_at || left.created_at || '');
        const rightTime = Date.parse(right.updated_at || right.created_at || '');

        if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
            return rightTime - leftTime;
        }

        return Number(right.id ?? 0) - Number(left.id ?? 0);
    });

const parseNumericIdList = (value: string): ValidationResult<number[]> => {
    const trimmed = value.trim();

    if (!trimmed) {
        return { valid: true, data: [] };
    }

    const parts = trimmed
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

    if (parts.length === 0) {
        return { valid: true, data: [] };
    }

    const ids = parts.map((part) => Number(part));

    if (ids.some((id) => !Number.isInteger(id) || id <= 0)) {
        return { valid: false, message: 'Image IDs must be a comma-separated list of positive integers.' };
    }

    return { valid: true, data: Array.from(new Set(ids)) };
};

export const toBoolean = (value: unknown): boolean =>
    value === true || value === 1 || value === '1';

export const extractApiErrorMessage = (err: any, fallback: string): string => {
    const message = err?.response?.data?.messages || err?.response?.data?.message || err?.message;

    if (Array.isArray(message)) {
        return message.join(', ');
    }

    return typeof message === 'string' && message.trim() ? message : fallback;
};

export const extractNewsletterCollection = (response: any): NewsletterRecord[] => {
    const payload = extractApiPayload(response);
    return sortByNewest(extractItemsArray<NewsletterRecord>(payload));
};

export const extractSubscriberCollection = (response: any): SubscriberRecord[] => {
    const payload = extractApiPayload(response);
    return sortByNewest(extractItemsArray<SubscriberRecord>(payload));
};

export const emptyNewsletterForm = (): NewsletterFormState => ({
    name: '',
    title: '',
    content: '',
    imageIds: '',
});

export const emptySubscriberForm = (): SubscriberFormState => ({
    email: '',
    active: true,
});

export const newsletterToFormState = (newsletter: NewsletterRecord): NewsletterFormState => ({
    name: newsletter.name || '',
    title: newsletter.title || '',
    content: newsletter.content || '',
    imageIds: Array.isArray(newsletter.files)
        ? newsletter.files
            .map((file) => Number(file?.id))
            .filter((id) => Number.isInteger(id) && id > 0)
            .join(', ')
        : '',
});

export const subscriberToFormState = (subscriber: SubscriberRecord): SubscriberFormState => ({
    email: subscriber.email || '',
    active: toBoolean(subscriber.active ?? true),
});

export const validateNewsletterForm = (form: NewsletterFormState): ValidationResult<ValidNewsletterForm> => {
    const name = form.name.trim();
    const title = form.title.trim();
    const content = form.content.trim();

    if (!name) {
        return { valid: false, message: 'Newsletter name is required.' };
    }

    if (!title) {
        return { valid: false, message: 'Newsletter title is required.' };
    }

    if (title.length > 100) {
        return { valid: false, message: 'Newsletter title must be 100 characters or fewer.' };
    }

    if (!content) {
        return { valid: false, message: 'Newsletter content is required.' };
    }

    const parsedImageIds = parseNumericIdList(form.imageIds);
    if (!parsedImageIds.valid) {
        return parsedImageIds;
    }

    return {
        valid: true,
        data: {
            name,
            title,
            content,
            ...(parsedImageIds.data.length > 0 ? { image_id: parsedImageIds.data } : {}),
        }
    };
};

export const validateSubscriberForm = (form: SubscriberFormState): ValidationResult<ValidSubscriberForm> => {
    const email = form.email.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
        return { valid: false, message: 'Subscriber email is required.' };
    }

    if (!emailPattern.test(email)) {
        return { valid: false, message: 'Please enter a valid email address.' };
    }

    return {
        valid: true,
        data: {
            email,
            active: form.active,
        }
    };
};

export const formatDateTime = (value?: string | null): string => {
    if (!value) {
        return 'N/A';
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(parsed);
};
